import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type CaptchaConfig = { enabled: boolean; app_id: string };

export default function Login() {
  const [, navigate] = useLocation();
  const { isAuthenticated, isLoading, login, user } = useAuth();
  const { platformTheme } = useTheme();
  const { t } = useLanguage();
  const isDark = platformTheme === "tech";

  const [captchaConfig, setCaptchaConfig] = useState<CaptchaConfig | null>(null);
  useEffect(() => {
    authApi
      .getCaptchaConfig()
      .then(setCaptchaConfig)
      .catch(() => setCaptchaConfig({ enabled: false, app_id: "" }));
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (user?.must_change_password) {
        navigate("/change-password");
      } else {
        navigate("/console/dashboard");
      }
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  if (isLoading) {
    return (
      <div className={cn("flex h-screen w-full items-center justify-center", isDark ? "bg-[#0a0e1a]" : "bg-slate-50")}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (isAuthenticated) return null;

  return (
    <div
      className={cn(
        "relative flex min-h-screen items-center justify-center px-4",
        isDark ? "bg-[#0a0e1a]" : "bg-slate-50"
      )}
      style={{
        backgroundImage: isDark
          ? "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(37,99,235,0.08), transparent 70%), radial-gradient(ellipse 50% 40% at 50% 100%, rgba(6,182,212,0.06), transparent 70%)"
          : "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(37,99,235,0.10), transparent 70%), radial-gradient(ellipse 50% 40% at 50% 100%, rgba(6,182,212,0.08), transparent 70%)",
      }}
    >
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl shadow-lg shadow-blue-500/30"
            style={{ background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 50%, #818CF8 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 3h6v8l4 7H5l4-7V3z" />
              <path d="M9 3h6" />
              <circle cx="12" cy="15" r="1" fill="white" stroke="none" />
            </svg>
          </div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Science<span className="text-blue-600">Token</span>
          </h1>
          <p className={cn("mt-2 text-sm", isDark ? "text-slate-400" : "text-slate-600")}>{t("login.title")}</p>
        </div>

        <div className={cn(
          "rounded-xl border p-6 shadow-xl",
          isDark
            ? "border-white/10 bg-white/5 backdrop-blur-sm shadow-black/20"
            : "border-slate-200 bg-white shadow-slate-200/60"
        )}>
          <LoginForm
            isDark={isDark}
            captchaConfig={captchaConfig}
            onLogin={async (email, password, captcha) => {
              const u = await login(email, password, captcha);
              if (u.must_change_password) {
                navigate("/change-password");
              } else {
                navigate("/console/dashboard");
              }
            }}
          />
          <p className={cn("mt-4 text-center text-xs", isDark ? "text-slate-500" : "text-slate-500")}>
            {t("login.footer")}
          </p>
        </div>
      </div>
    </div>
  );
}

function LoginForm({
  isDark,
  captchaConfig,
  onLogin,
}: {
  isDark: boolean;
  captchaConfig: CaptchaConfig | null;
  onLogin: (
    email: string,
    password: string,
    captcha?: { ticket: string; randstr: string },
  ) => Promise<void>;
}) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const captchaMountRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t("login.fillAll"));
      return;
    }
    if (!captchaConfig) {
      toast.error(t("login.captcha.loading"));
      return;
    }

    if (!captchaConfig.enabled) {
      await runLogin();
      return;
    }

    if (typeof window.TencentCaptcha !== "function") {
      toast.error(t("login.captcha.notLoaded"));
      return;
    }

    setLoading(true);
    try {
      if (!captchaMountRef.current) {
        toast.error(t("login.captcha.failed"));
        setLoading(false);
        return;
      }
      const captcha = new window.TencentCaptcha(
        captchaMountRef.current,
        captchaConfig.app_id,
        async (res) => {
          try {
            if (res.ret === 0) {
              try {
                await onLogin(email, password, {
                  ticket: res.ticket,
                  randstr: res.randstr,
                });
                toast.success(t("login.success"));
              } catch (err: unknown) {
                handleLoginError(err);
              }
              return;
            }
            if (res.ret === 2) {
              toast.message(t("login.captcha.cancelled"));
            } else {
              toast.error(t("login.captcha.failed"));
            }
          } finally {
            setLoading(false);
            captcha.destroy?.();
          }
        },
        {},
      );
      captcha.show();
    } catch {
      setLoading(false);
      toast.error(t("login.captcha.failed"));
    }
  };

  const runLogin = async () => {
    setLoading(true);
    try {
      await onLogin(email, password);
      toast.success(t("login.success"));
    } catch (err: unknown) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginError = (err: unknown) => {
    const e = err as { response?: { data?: { message?: string } }; message?: string };
    const msg = e?.response?.data?.message || e?.message || t("login.failed");
    toast.error(msg);
  };

  const inputClass = cn(
    "h-10 focus-visible:border-blue-500 focus-visible:ring-blue-500/30",
    isDark
      ? "border-white/10 bg-white/5 text-white placeholder:text-slate-500"
      : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400"
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email" className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
          {t("login.email")}
        </Label>
        <Input
          id="login-email"
          type="email"
          placeholder={t("login.placeholder.email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password" className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>
          {t("login.password")}
        </Label>
        <Input
          id="login-password"
          type="password"
          placeholder={t("login.placeholder.password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
        />
      </div>
      <Button
        type="submit"
        className="h-10 w-full btn-primary-sci"
        disabled={loading}
      >
        {loading ? t("login.loading") : t("login.button")}
      </Button>
      <div ref={captchaMountRef} className="captcha-mount" aria-hidden="true" />
    </form>
  );
}
